// Shared edit page mounted by all four edit routes (admin, circles,
// networks, groups). Fetches the broadcast via `useBroadcastDetail`,
// handles loading / error / "sent (not editable)" states, then hands the
// row to `BroadcastEditForm`.

import { useState } from 'react';
import { PageHeader, Stack } from '@leanin/ui';

import { ErrorState } from '@leanin/ui';
import { LoadErrorState, LoadingState } from '@/components/states/index';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';
import {
  useBroadcastDetail,
  type BroadcastScope,
} from '@/lib/queries/broadcasts';

import { isBroadcastSending } from '@/components/admin/BroadcastStatusPill';
import { BroadcastEditForm } from './BroadcastEditForm';
import type { BroadcastAudienceContext } from './BroadcastEditorFields';
import { useTranslations } from 'use-intl';

export interface EditBroadcastPageProps {
  readonly scope: BroadcastScope;
  readonly broadcastId: string;
  readonly audienceFromScope: BroadcastAudienceContext;
}

export function EditBroadcastPage({
  scope,
  broadcastId,
  audienceFromScope,
}: EditBroadcastPageProps) {
  const tAdmin = useTranslations('Admin.broadcasts');
  const q = useBroadcastDetail(scope, broadcastId);
  // Bumped once a save conflict's refetch has landed; see the form's key.
  const [conflictReloads, setConflictReloads] = useState(0);

  if (isNotFoundApiError(q.error)) return <NotFoundPage />;
  if (q.isError) {
    return (
      <LoadErrorState title={tAdmin('loadOneError')} error={q.error} />
    );
  }

  if (q.data === undefined) {
    return (
      <Stack gap="xl">
        <PageHeader title="Loading…" />
        <LoadingState rows={3} label="Loading broadcast" />
      </Stack>
    );
  }

  if (q.data.status === 'sent') {
    return (
      <Stack gap="xl">
        <PageHeader title={q.data.subject} />
        <ErrorState
          title="This broadcast can't be edited"
          body="Sent broadcasts are read-only. Duplicate it to start a new draft."
        />
      </Stack>
    );
  }

  // A scheduled broadcast whose send time has passed is mid/awaiting dispatch
  // ("Sending") — no longer editable, matching the list's read-only routing.
  // (A still-future scheduled broadcast stays editable.)
  if (isBroadcastSending(q.data.status, q.data.scheduledSendAt)) {
    return (
      <Stack gap="xl">
        <PageHeader title={q.data.subject} />
        <ErrorState
          title="This broadcast is being sent"
          body="It's already going out to members and can't be edited. Duplicate it to start a new draft."
        />
      </Stack>
    );
  }

  return (
    <Stack gap="xl">
      <PageHeader title={q.data.subject} />
      {/* Remounted only after a save conflict, from the refetched server
          copy: the form's own `version` (and edits) are stale by then, and
          every later save would conflict again. A successful save keeps the
          form mounted — it adopts the new version itself. */}
      <BroadcastEditForm
        key={`${q.data.id}:${conflictReloads}`}
        audience={audienceFromScope}
        draft={q.data}
        reloadedAfterConflict={conflictReloads > 0}
        onVersionConflict={() => setConflictReloads((n) => n + 1)}
      />
    </Stack>
  );
}
